import React, { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, ChevronUp, X, Search, Wheat, MapPin, CloudSun, Droplets, Layers, Sprout, Pipette } from 'lucide-react';

// ── Water Quantity Baselines (m³/Feddan/Week) ──
const WATER_BASELINES = {
  drip: 25,
  sprinkler: 35,
  flood: 50,
  pivot: 30
};

const CONTEXT_STORAGE_KEY = 'geonutria_global_context';

function loadSavedContext() {
  try {
    return JSON.parse(localStorage.getItem(CONTEXT_STORAGE_KEY) || '{}');
  } catch { return {}; }
}

function saveContext(ctx) {
  try {
    localStorage.setItem(CONTEXT_STORAGE_KEY, JSON.stringify(ctx));
  } catch {}
}

// ── Searchable Combobox Sub-Component ──
function SearchableCombobox({ label, icon: Icon, options, value, onChange, placeholder, searchPlaceholder, noResultsText, clearText, isRTL, disabled, allowCustom }) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [highlightIdx, setHighlightIdx] = useState(-1);
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  const filtered = useMemo(() =>
    options.filter(opt =>
      opt.label.toLowerCase().includes(search.toLowerCase())
    ), [options, search]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
        setSearch('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => { setHighlightIdx(-1); }, [search, isOpen]);

  const handleKeyDown = (e) => {
    if (!isOpen) {
      if (e.key === 'ArrowDown' || e.key === 'Enter') {
        e.preventDefault();
        setIsOpen(true);
      }
      return;
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setHighlightIdx(i => Math.min(i + 1, filtered.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setHighlightIdx(i => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (highlightIdx >= 0 && filtered[highlightIdx]) {
        onChange(filtered[highlightIdx].key);
      } else if (allowCustom && search.trim()) {
        onChange(search.trim());
      }
      setIsOpen(false);
      setSearch('');
    } else if (e.key === 'Escape') {
      setIsOpen(false);
      setSearch('');
    }
  };

  const selectedLabel = options.find(o => o.key === value)?.label || value || '';

  return (
    <div className={`relative ${isOpen ? 'z-50' : 'z-10'}`} ref={containerRef}>
      <label className="block text-[0.65rem] font-bold uppercase tracking-wider text-[#1B263B]/50 dark:text-white/40 mb-1.5 flex items-center gap-1.5">
        {Icon && <Icon size={12} className="text-[#C47A2C] opacity-70" />}
        {label}
      </label>
      <div
        className={`flex items-center gap-2 w-full bg-white/70 dark:bg-slate-800/70 border rounded-xl px-3 py-2 text-sm font-medium cursor-pointer transition-all
          ${isOpen
            ? 'border-[#C47A2C]/50 ring-2 ring-[#C47A2C]/10 shadow-md'
            : 'border-[#1B263B]/10 dark:border-slate-700 hover:border-[#C47A2C]/30 shadow-sm'
          }
          ${disabled ? 'opacity-40 cursor-not-allowed' : ''}
        `}
        onClick={() => { if (!disabled) { setIsOpen(!isOpen); setTimeout(() => inputRef.current?.focus(), 50); } }}
      >
        {value ? (
          <div className="flex items-center justify-between w-full min-w-0">
            <span className="text-[#1B263B] dark:text-white truncate">{selectedLabel}</span>
            <button
              onClick={(e) => { e.stopPropagation(); onChange(''); }}
              className="p-0.5 rounded-full hover:bg-rose-100 dark:hover:bg-rose-500/10 text-rose-400 hover:text-rose-500 transition-colors shrink-0 ltr:ml-1 rtl:mr-1"
            >
              <X size={12} />
            </button>
          </div>
        ) : (
          <span className="text-gray-400 dark:text-gray-600 flex-1">{placeholder}</span>
        )}
        <ChevronDown size={14} className={`text-gray-400 shrink-0 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </div>

      <AnimatePresence>
        {isOpen && !disabled && (
          <motion.div
            initial={{ opacity: 0, y: -4, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -4, scale: 0.98 }}
            transition={{ duration: 0.15 }}
            className={`absolute z-50 w-full mt-1.5 bg-white dark:bg-slate-800 border border-[#1B263B]/10 dark:border-slate-700 rounded-xl shadow-xl overflow-hidden`}
            style={{ [isRTL ? 'right' : 'left']: 0 }}
          >
            {/* Search input */}
            <div className="p-2 border-b border-gray-100 dark:border-slate-700">
              <div className="flex items-center gap-2 bg-gray-50 dark:bg-slate-900 rounded-lg px-2.5 py-1.5">
                <Search size={13} className="text-gray-400 shrink-0" />
                <input
                  ref={inputRef}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder={searchPlaceholder}
                  className="bg-transparent border-none outline-none text-sm text-[#1B263B] dark:text-white placeholder:text-gray-400 dark:placeholder:text-gray-600 w-full"
                  dir="auto"
                />
              </div>
            </div>
            {/* Options list */}
            <div className="max-h-48 overflow-y-auto custom-scrollbar">
              {allowCustom && search.trim() && !options.some(o => o.label.toLowerCase() === search.trim().toLowerCase()) && (
                <button
                  onMouseEnter={() => setHighlightIdx(-1)}
                  onClick={() => { onChange(search.trim()); setIsOpen(false); setSearch(''); }}
                  className={`w-full text-start px-3.5 py-2 text-sm transition-colors flex items-center gap-2 ${highlightIdx === -1 ? 'bg-gray-50 dark:bg-white/5 text-[#1B263B] dark:text-white' : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-white/5'}`}
                >
                  <span className="opacity-70 text-xs">{isRTL ? "إضافة:" : "Create:"}</span> <strong>"{search.trim()}"</strong>
                </button>
              )}
              {filtered.length === 0 && (!allowCustom || !search.trim()) ? (
                <p className="text-center text-xs text-gray-400 dark:text-gray-600 py-4">{noResultsText}</p>
              ) : (
                filtered.map((opt, idx) => (
                  <button
                    key={opt.key}
                    onMouseEnter={() => setHighlightIdx(idx)}
                    onClick={() => { onChange(opt.key); setIsOpen(false); setSearch(''); }}
                    className={`w-full text-start px-3.5 py-2 text-sm transition-colors flex items-center gap-2
                      ${opt.key === value ? 'bg-[#C47A2C]/10 text-[#C47A2C] font-semibold' :
                        idx === highlightIdx ? 'bg-gray-50 dark:bg-white/5 text-[#1B263B] dark:text-white' :
                        'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-white/5'}`}
                  >
                    {opt.label}
                  </button>
                ))
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}


// ── Main GlobalContextBar Component ──
export default function GlobalContextBar({ t, isRTL, onContextChange, hideCropFields = false }) {
  const gc = t?.globalContext || {};

  const [isExpanded, setIsExpanded] = useState(false);

  const [overflowState, setOverflowState] = useState(isExpanded ? 'visible' : 'hidden');

  const [context, setContext] = useState(() => {
    const saved = loadSavedContext();
    return {
      cropType: saved.cropType || '',
      cropVariety: saved.cropVariety || '',
      location: saved.location || '',
      season: saved.season || '',
      irrigationType: saved.irrigationType || '',
      waterQuantity: saved.waterQuantity || '',
      soilType: saved.soilType || '',
      analysisDepth: saved.analysisDepth || ''
    };
  });

  // Reset overflow to hidden immediately when collapsing starts
  useEffect(() => {
    if (!isExpanded) {
      setOverflowState('hidden');
    }
  }, [isExpanded]);

  // Persist & notify parent on change
  useEffect(() => {
    saveContext(context);
    if (onContextChange) onContextChange(context);
  }, [context, onContextChange]);

  // Auto-fill water quantity when irrigation type changes
  useEffect(() => {
    if (context.irrigationType && WATER_BASELINES[context.irrigationType] !== undefined) {
      setContext(prev => ({ ...prev, waterQuantity: WATER_BASELINES[context.irrigationType] }));
    }
  }, [context.irrigationType]);

  // Reset variety when crop type changes
  useEffect(() => {
    setContext(prev => ({ ...prev, cropVariety: '' }));
  }, [context.cropType]);

  const updateField = useCallback((field, value) => {
    setContext(prev => ({ ...prev, [field]: value }));
  }, []);

  // Build option lists from translations
  const cropOptions = useMemo(() =>
    Object.entries(gc.crops || {}).map(([key, label]) => ({ key, label })),
  [gc.crops]);

  const varietyOptions = useMemo(() => {
    if (!context.cropType || !gc.varieties?.[context.cropType]) return [];
    return Object.entries(gc.varieties[context.cropType]).map(([key, label]) => ({ key, label }));
  }, [context.cropType, gc.varieties]);

  const locationOptions = useMemo(() =>
    Object.entries(gc.locations || {}).map(([key, label]) => ({ key, label })),
  [gc.locations]);

  const seasonOptions = useMemo(() =>
    Object.entries(gc.seasons || {}).map(([key, label]) => ({ key, label })),
  [gc.seasons]);

  const irrigationOptions = useMemo(() =>
    Object.entries(gc.irrigationTypes || {}).map(([key, label]) => ({ key, label })),
  [gc.irrigationTypes]);

  const soilOptions = useMemo(() =>
    Object.entries(gc.soilTypes || {}).map(([key, label]) => ({ key, label })),
  [gc.soilTypes]);

  const analysisDepthOptions = useMemo(() => {
    if (!gc.analysisDepthOptions) return [];
    return Object.entries(gc.analysisDepthOptions).map(([key, label]) => ({ key, label }));
  }, [gc.analysisDepthOptions]);

  // Count of filled fields (for badge)
  const filledCount = Object.values(context).filter(v => v !== '' && v !== undefined).length;

  return (
    <div className="col-span-12 mb-2 relative z-20">
      {/* Toggle Button */}
      <motion.button
        whileHover={{ scale: 1.01 }} whileTap={{ scale: 0.99 }}
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between px-4 py-2.5 bg-gradient-to-r from-[#C47A2C]/[0.06] to-[#6B8F71]/[0.06] dark:from-[#C47A2C]/[0.08] dark:to-[#6B8F71]/[0.08] hover:from-[#C47A2C]/[0.10] hover:to-[#6B8F71]/[0.10] border border-[#C47A2C]/15 dark:border-[#C47A2C]/20 rounded-xl transition-all group"
      >
        <div className="flex items-center gap-2.5">
          <div className="w-7 h-7 rounded-lg bg-gradient-to-br from-[#C47A2C] to-[#6B8F71] flex items-center justify-center shadow-sm">
            <Layers size={14} className="text-white" />
          </div>
          <span className="text-sm font-bold text-[#1B263B] dark:text-white">{gc.sectionTitle || 'Farm Context'}</span>
          {filledCount > 0 && (
            <span className="px-2 py-0.5 text-[0.6rem] font-bold rounded-full bg-[#C47A2C]/15 text-[#C47A2C]">
              {filledCount}/8
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 text-xs font-medium text-gray-400 dark:text-gray-500">
          <span>{isExpanded ? gc.collapse : gc.expand}</span>
          {isExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
        </div>
      </motion.button>

      {/* Collapsible Content */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25, ease: 'easeInOut' }}
            onAnimationComplete={() => {
              if (isExpanded) setOverflowState('visible');
            }}
            style={{ overflow: overflowState }}
          >
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mt-3 p-4 bg-white/50 dark:bg-slate-800/30 border border-[#1B263B]/5 dark:border-slate-700/50 rounded-xl backdrop-blur-sm">

              {/* 1. Crop Type */}
              {!hideCropFields && (
                <SearchableCombobox
                  label={gc.cropType}
                  icon={Wheat}
                  options={cropOptions}
                  value={context.cropType}
                  onChange={(v) => updateField('cropType', v)}
                  placeholder={gc.selectPlaceholder}
                  searchPlaceholder={gc.searchPlaceholder}
                  noResultsText={gc.noResults}
                  clearText={gc.clearSelection}
                  isRTL={isRTL}
                  allowCustom={true}
                />
              )}

              {/* 2. Crop Variety (dynamic) */}
              {!hideCropFields && (
                <SearchableCombobox
                  label={gc.cropVariety}
                  icon={Sprout}
                  options={varietyOptions}
                  value={context.cropVariety}
                  onChange={(v) => updateField('cropVariety', v)}
                  placeholder={gc.selectPlaceholder}
                  searchPlaceholder={gc.searchPlaceholder}
                  noResultsText={gc.noResults}
                  clearText={gc.clearSelection}
                  isRTL={isRTL}
                  disabled={!context.cropType}
                  allowCustom={true}
                />
              )}

              {/* 3. Geographic Location */}
              <SearchableCombobox
                label={gc.location}
                icon={MapPin}
                options={locationOptions}
                value={context.location}
                onChange={(v) => updateField('location', v)}
                placeholder={gc.selectPlaceholder}
                searchPlaceholder={gc.searchPlaceholder}
                noResultsText={gc.noResults}
                clearText={gc.clearSelection}
                isRTL={isRTL}
                allowCustom={true}
              />

              {/* 4. Current Season */}
              <SearchableCombobox
                label={gc.season}
                icon={CloudSun}
                options={seasonOptions}
                value={context.season}
                onChange={(v) => updateField('season', v)}
                placeholder={gc.selectPlaceholder}
                searchPlaceholder={gc.searchPlaceholder}
                noResultsText={gc.noResults}
                clearText={gc.clearSelection}
                isRTL={isRTL}
              />

              {/* 5. Irrigation Type */}
              <SearchableCombobox
                label={gc.irrigationType}
                icon={Droplets}
                options={irrigationOptions}
                value={context.irrigationType}
                onChange={(v) => updateField('irrigationType', v)}
                placeholder={gc.selectPlaceholder}
                searchPlaceholder={gc.searchPlaceholder}
                noResultsText={gc.noResults}
                clearText={gc.clearSelection}
                isRTL={isRTL}
              />

              {/* 6. Water Quantity (numeric input) */}
              <div className="relative">
                <label className="block text-[0.65rem] font-bold uppercase tracking-wider text-[#1B263B]/50 dark:text-white/40 mb-1.5 flex items-center gap-1.5">
                  <Pipette size={12} className="text-[#C47A2C] opacity-70" />
                  {gc.waterQuantity}
                </label>
                <div className="flex items-center gap-2 w-full bg-white/70 dark:bg-slate-800/70 border border-[#1B263B]/10 dark:border-slate-700 rounded-xl px-3 py-2 text-sm font-medium shadow-sm transition-all focus-within:border-[#C47A2C]/50 focus-within:ring-2 focus-within:ring-[#C47A2C]/10">
                  <input
                    type="number"
                    min="0"
                    step="0.5"
                    value={context.waterQuantity}
                    onChange={(e) => updateField('waterQuantity', e.target.value)}
                    placeholder="0"
                    className="bg-transparent border-none outline-none text-[#1B263B] dark:text-white w-full appearance-none [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none [-moz-appearance:textfield]"
                  />
                  <span className="text-[0.6rem] font-semibold text-gray-400 dark:text-gray-600 whitespace-nowrap">{gc.waterUnit}</span>
                </div>
              </div>

              {/* 7. Soil Type */}
              <SearchableCombobox
                label={gc.soilType}
                icon={Layers}
                options={soilOptions}
                value={context.soilType}
                onChange={(v) => updateField('soilType', v)}
                placeholder={gc.selectPlaceholder}
                searchPlaceholder={gc.searchPlaceholder}
                noResultsText={gc.noResults}
                clearText={gc.clearSelection}
                isRTL={isRTL}
              />

              {/* 8. Analysis Response Depth */}
              <SearchableCombobox
                label={gc.analysisDepth}
                icon={ChevronDown}
                options={analysisDepthOptions}
                value={context.analysisDepth}
                onChange={(v) => updateField('analysisDepth', v)}
                placeholder={gc.selectPlaceholder}
                searchPlaceholder={gc.searchPlaceholder}
                noResultsText={gc.noResults}
                clearText={gc.clearSelection}
                isRTL={isRTL}
              />

            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
